import { useMutation, useQueryClient } from '@tanstack/react-query';
import API_ENDPOINTS from './endpoints';
import { trackCompletedRegistration } from 'utils/googleAds';

export const useFringeMutation = {
  useCreateFringeMutation: () => {
    return useMutation(payload => API_ENDPOINTS.fringes.create_fringe(payload));
  },
  useDeleteFringeMutation: () => {
    return useMutation(payload => API_ENDPOINTS.fringes.delete_fringe(payload));
  },
  useUpdateFringeMutation: () => {
    return useMutation(payload => API_ENDPOINTS.fringes.update_fringe(payload));
  },
  useDuplicateFringeMutation: () => {
    return useMutation(id => API_ENDPOINTS.fringes.duplicate_fringe(id));
  },
};

export const useGlobalMutation = {
  useCreateGlobalMutation: () => {
    return useMutation(payload => API_ENDPOINTS.globals.create_global(payload));
  },
  useUpdateGlobalMutation: () => {
    return useMutation(payload => API_ENDPOINTS.globals.update_global(payload));
  },
  useDeleteGlobalMutation: () => {
    return useMutation(id => API_ENDPOINTS.globals.delete_global(id));
  },
  useImportGlobalToUserMutation: () => {
    return useMutation(id => API_ENDPOINTS.globals.import_to_user(id));
  },
  useImportGlobalToBudgetMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.globals.import_to_budget(payload)
    );
  },
};

export const useTagMutation = {
  useCreateTagMutation: () => {
    return useMutation(payload => API_ENDPOINTS.tags.create_tag(payload));
  },
  useUpdateTagMutation: () => {
    return useMutation(payload => API_ENDPOINTS.tags.update_tag(payload));
  },
  useDeleteTagMutation: () => {
    return useMutation(id => API_ENDPOINTS.tags.delete_tag(id));
  },
};

export const useGroupMutation = {
  useCreateGroupMutation: () => {
    return useMutation(payload => API_ENDPOINTS.groups.create_group(payload));
  },
  useDeleteGroupMutation: () => {
    return useMutation(payload => API_ENDPOINTS.groups.delete_group(payload));
  },
  useUpdateGroupMutation: () => {
    return useMutation(payload => API_ENDPOINTS.groups.update_group(payload));
  },
  useAddFringeToGroupMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.groups.add_fringe_to_group(payload)
    );
  },
  useRemoveFringeFromGroupMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.groups.remove_fringe_from_group(payload)
    );
  },
  useDuplicateGroupMutation: () => {
    return useMutation(id => API_ENDPOINTS.groups.duplicate_group(id));
  },
};

export const useOnboardingMutation = {
  useVerifyEmailMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.onboarding.verify_email(payload)
    );
  },
  useSignupMutation: () => {
    return useMutation(
      payload => API_ENDPOINTS.onboarding.user_signup(payload),
      { onSuccess: response => trackCompletedRegistration(response?.data) }
    );
  },
  useResendOTPMutation: () => {
    return useMutation(payload => API_ENDPOINTS.onboarding.resend_otp(payload));
  },
  useVerifyOTPMutation: () => {
    return useMutation(payload => API_ENDPOINTS.onboarding.verify_otp(payload));
  },
  useLoginMutation: () => {
    return useMutation(payload => API_ENDPOINTS.onboarding.user_login(payload));
  },
  useForgotPasswordMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.onboarding.forgot_password(payload)
    );
  },
  useResendForgotPasswordMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.onboarding.forgot_password_resend_otp(payload)
    );
  },
  useVerifyForgotPasswordMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.onboarding.forgot_password_verify_otp(payload)
    );
  },
  useSocialLoginMutation: () => {
    return useMutation(
      ({ provider, payload }) =>
        API_ENDPOINTS.onboarding.social_login(provider, payload),
      { onSuccess: response => trackCompletedRegistration(response?.data) }
    );
  },
};

export const useAccountSettingMutation = {
  useChangePasswordMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.account_settings.change_password(payload)
    );
  },
  useRequestEmailChangeMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.account_settings.request_email_change(payload)
    );
  },
  useConfirmEmailChangeMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.account_settings.confirm_email_change(payload)
    );
  },
  useLogoutMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.account_settings.user_logout(payload)
    );
  },
  useDeleteAccountMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.account_settings.delete_account(payload)
    );
  },
  useUserProfileMutation: () => {
    return useMutation(payload => {
      if (payload?.id) {
        return API_ENDPOINTS.account_settings.update_user_profile(payload);
      } else {
        return API_ENDPOINTS.account_settings.create_user_profile(payload);
      }
    });
  },
};

export const useBudgetMutation = {
  useApplyBudgetHistoryMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.budgets.apply_budget_history(payload)
    );
  },
  useCreateCellCommentMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.budgets.create_cell_comment(payload)
    );
  },
  useUpdateCellCommentMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.budgets.update_cell_comment(payload)
    );
  },
  useDeleteCellCommentMutation: () => {
    return useMutation(id => API_ENDPOINTS.budgets.delete_cell_comment(id));
  },
  useCreateBudgetCommentMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.budgets.create_budget_comment(payload)
    );
  },
  useUpdateBudgetCommentMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.budgets.update_budget_comment(payload)
    );
  },
  useDeleteBudgetCommentMutation: () => {
    return useMutation(id => API_ENDPOINTS.budgets.delete_budget_comment(id));
  },
  useMarkNotificationReadMutation: () => {
    return useMutation(id => API_ENDPOINTS.budgets.mark_notification_read(id));
  },
  useMarkCommentNotificationsReadMutation: () => {
    const queryClient = useQueryClient();
    return useMutation(
      payload => API_ENDPOINTS.budgets.mark_comment_notifications_read(payload),
      {
        onSuccess: () =>
          queryClient.invalidateQueries({
            queryKey: ['budget-notifications'],
          }),
      }
    );
  },
  useCreateBudgetMutation: () => {
    return useMutation(payload =>
      payload?.id
        ? API_ENDPOINTS.budgets.update_budget(payload)
        : API_ENDPOINTS.budgets.create_budget(payload)
    );
  },
  useCreateFolderMutation: () => {
    return useMutation(payload => API_ENDPOINTS.budgets.create_folder(payload));
  },

  useRenameFolderMutuation: () => {
    return useMutation(payload => API_ENDPOINTS.budgets.update_folder(payload));
  },

  useMoveBudgetMutation: () => {
    return useMutation(payload => API_ENDPOINTS.budgets.move_budget(payload));
  },

  useMoveBudgetFolderMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.budgets.move_budget_folder(payload)
    );
  },

  useDuplicateBudgetMutation: () => {
    return useMutation(payload =>
      payload?.withValues
        ? API_ENDPOINTS.budgets.duplicate_budget_values(payload)
        : API_ENDPOINTS.budgets.duplicate_budget(payload)
    );
  },
  useDeleteBudgetMutation: () => {
    return useMutation(budgetId =>
      API_ENDPOINTS.budgets.delete_budget(budgetId)
    );
  },
  useCreateSwimlaneMutation: () => {
    return useMutation(
      ({ type, swimlane, budgetId, name, position, selectedItem, section }) => {
        let payload;
        let endpoint;
        if (type === 'swimlane') {
          payload = {
            budget: budgetId,
            name,
            position,
            section: section || null,
          };
          endpoint = API_ENDPOINTS.budgets.create_swimlane;
        } else if (type === 'line-item') {
          payload = {
            budget: budgetId,
            parent: swimlane || selectedItem?.id,
            name,
            position,
          };
          endpoint = API_ENDPOINTS.budgets.create_line_item;
        } else if (type === 'line-item-children') {
          payload = {
            budget: budgetId,
            parent: selectedItem?.id || swimlane,
            name,
            position,
          };
          endpoint = API_ENDPOINTS.budgets.create_line_item_children;
        }
        if (!endpoint || !payload) {
          throw new Error('Unknown category type');
        }
        return endpoint(payload);
      }
    );
  },
  useCreateContractualChargeMutation: () => {
    return useMutation(
      payload => API_ENDPOINTS.budgets.create_contractual_charge(payload),
      {
        // Prevent duplicate submissions
        retry: false,
      }
    );
  },
  useUpdateContractualChargeMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.budgets.update_contractual_charge(payload)
    );
  },
  useDeleteContractualChargeMutation: () => {
    return useMutation(id =>
      API_ENDPOINTS.budgets.delete_contractual_charge(id)
    );
  },
  useRepositionContractualChargesMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.budgets.reposition_contractual_charges(payload)
    );
  },
  useCreateFilmIncentiveMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.budgets.create_film_incentive(payload)
    );
  },
  useUpdateFilmIncentiveMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.budgets.update_film_incentive(payload)
    );
  },
  useDeleteFilmIncentiveMutation: () => {
    return useMutation(id => API_ENDPOINTS.budgets.delete_film_incentive(id));
  },
  useUpdateSwimlaneMutation: () => {
    return useMutation(({ type, name, cost, id }) => {
      let payload;
      let endpoint;
      if (type === 'swimlane') {
        payload = {
          id,
          name,
          cost,
        };
        endpoint = API_ENDPOINTS.budgets.update_swimlane;
      } else if (type === 'line-item') {
        payload = {
          id,
          name,
          cost,
        };
        endpoint = API_ENDPOINTS.budgets.update_line_item;
      } else if (type === 'line-item-children') {
        payload = {
          id,
          name,
          cost,
        };
        endpoint = API_ENDPOINTS.budgets.update_line_item_children;
      }
      return endpoint(payload);
    });
  },
  useDeleteSwimlaneMutation: () => {
    return useMutation(({ type, id }) => {
      let endpoint;
      if (type === 'swimlane') {
        endpoint = API_ENDPOINTS.budgets.delete_swimlane;
      } else if (type === 'line-item') {
        endpoint = API_ENDPOINTS.budgets.delete_line_item;
      } else if (type === 'line-item-children') {
        endpoint = API_ENDPOINTS.budgets.delete_line_item_children;
      }
      return endpoint(id);
    });
  },
  useAddSubcategoryMutation: () => {
    return useMutation(({ type, id }) => {
      let endpoint;
      if (type === 'swimlane') {
        endpoint = API_ENDPOINTS.budgets.add_sub_category_swimlane;
      } else if (type === 'line-item') {
        endpoint = API_ENDPOINTS.budgets.add_sub_category_line_item;
      } else if (type === 'line-item-children') {
        endpoint = API_ENDPOINTS.budgets.add_sub_category_line_item;
      }
      return endpoint(id);
    });
  },
  useMoveLineItemCategoryMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.move_line_item_category.move_line_item_category(payload)
    );
  },
  useMoveLineItemCategoriesMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.move_line_item_category.move_line_item_categories(payload)
    );
  },
  useMoveDetailRowMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.budgets.move_detail_row(payload)
    );
  },
  useResetDetailViewMutation: () => {
    return useMutation(({ type, id }) => {
      let endpoint;
      if (type === 'swimlane') {
        endpoint = API_ENDPOINTS.budgets.reset_detail_view_swimlane;
      } else if (type === 'line-item') {
        endpoint = API_ENDPOINTS.budgets.reset_detail_view_line_item;
      } else if (type === 'line-item-children') {
        endpoint = API_ENDPOINTS.budgets.reset_detail_view_line_item_children;
      }
      return endpoint(id);
    });
  },
  useCreateSwimlaneDetailsMutation: () => {
    return useMutation(({ type, selectedItem, details, signal }) => {
      const config = signal ? { signal } : {};
      let payload;
      let endpoint;
      if (type === 'swimlane') {
        payload = {
          ...details,
          category: selectedItem.id,
        };
        endpoint = API_ENDPOINTS.budgets.create_swimlane_detail_sheet;
      } else if (type === 'line-item') {
        payload = {
          ...details,
          category: selectedItem.id,
        };
        endpoint = API_ENDPOINTS.budgets.create_line_item_detailed_sheet;
      } else if (type === 'line-item-children') {
        payload = {
          ...details,
          category: selectedItem.id,
        };
        endpoint =
          API_ENDPOINTS.budgets.create_line_item_children_detailed_sheet;
      }

      return endpoint(payload, config);
    });
  },
  useUpdateSwimlaneDetailsMutation: () => {
    return useMutation(({ type, selectedItem, details, signal }) => {
      const config = signal ? { signal } : {};
      let payload;
      let endpoint;
      if (type === 'swimlane') {
        payload = {
          ...details,
          category: selectedItem.id,
        };
        endpoint = API_ENDPOINTS.budgets.update_swimlane_detail_sheet;
      } else if (type === 'line-item') {
        payload = {
          ...details,
          category: selectedItem.id,
        };
        endpoint = API_ENDPOINTS.budgets.update_line_item_detailed_sheet;
      } else if (type === 'line-item-children') {
        payload = {
          ...details,
          category: selectedItem.id,
        };
        endpoint =
          API_ENDPOINTS.budgets.update_line_item_children_detailed_sheet;
      }

      return endpoint(payload, config);
    });
  },
  useDeleteSwimlaneDetailsMutation: () => {
    return useMutation(({ type, id, signal }) => {
      const config = signal ? { signal } : {};
      let endpoint;
      if (type === 'swimlane') {
        endpoint = API_ENDPOINTS.budgets.delete_swimlane_details_sheet;
      } else if (type === 'line-item') {
        endpoint = API_ENDPOINTS.budgets.delete_line_item_details_sheet;
      } else if (type === 'line-item-children') {
        endpoint =
          API_ENDPOINTS.budgets.delete_line_item_children_details_sheet;
      }

      return endpoint(id, config);
    });
  },
  useShareBudgetMutation: () => {
    return useMutation(payload => {
      return API_ENDPOINTS.budgets.share_budget(payload);
    });
  },
  useUpdatePublicBudgetLinkMutation: () => {
    return useMutation(payload => {
      return API_ENDPOINTS.budgets.update_public_budget_link_settings(payload);
    });
  },
  useRemoveBudgetAccessMutation: () => {
    return useMutation(payload => {
      return API_ENDPOINTS.budgets.remove_budget_access(payload);
    });
  },
  useRemoveMultiBudgetsMutuation: () => {
    return useMutation(payload => {
      return API_ENDPOINTS.budgets.delete_multi_budgets(payload);
    });
  },
  useRemoveFolderMutuation: () => {
    return useMutation(payload => {
      return API_ENDPOINTS.budgets.delete_folder(payload);
    });
  },
  useBudgetDownloadMutation: () => {
    return useMutation(payload => {
      return API_ENDPOINTS.budgets.download_budget(payload);
    });
  },
  useHoverBudgetMutation: () => {
    return useMutation(payload => {
      return API_ENDPOINTS.budgets.hover_budget(payload);
    });
  },
};

export const useTabsMutuation = {
  useCreateTabMutation: () => {
    return useMutation(payload => API_ENDPOINTS.tabs.create_tabs(payload));
  },
  useMakeActiveTabMutation: () => {
    return useMutation(payload => API_ENDPOINTS.tabs.make_active_tab(payload));
  },
  useReorderTabsMutation: () => {
    return useMutation(payload => API_ENDPOINTS.tabs.reorder_tabs(payload));
  },
  useCloseTabMutation: () => {
    return useMutation(tabId => API_ENDPOINTS.tabs.close_tab(tabId));
  },
};

export const useUserActivityMutation = {
  usePostUserActivityMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.user_activity.post_user_activity(payload)
    );
  },
};

export const usePostReorderSwimlane = {
  usePostReorderSwimlane: () => {
    return useMutation(payload =>
      API_ENDPOINTS.reorder_swimlane.reorder_swimlane(payload)
    );
  },
};

export const usePostReorderLineItem = {
  usePostReorderLineItem: () => {
    return useMutation(payload =>
      API_ENDPOINTS.reorder_line_item.reorder_line_item(payload)
    );
  },
};

export const usePostReorderDetailSheet = {
  usePostReorderDetailSheet: () => {
    return useMutation(payload =>
      API_ENDPOINTS.reorder_detail_sheet.reorder_detail_sheet(payload)
    );
  },
};

export const usePostReorderLineItemDetailSheet = {
  usePostReorderLineItemDetailSheet: () => {
    return useMutation(payload =>
      API_ENDPOINTS.reorder_line_item_detail_sheet.reorder_line_item_detail_sheet(
        payload
      )
    );
  },
};

export const useTopLevelSectionMutation = {
  useCreateTopLevelSectionMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.top_level_sections.create_section(payload)
    );
  },
  useUpdateTopLevelSectionMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.top_level_sections.update_section(payload)
    );
  },
  useDeleteTopLevelSectionMutation: () => {
    return useMutation(id =>
      API_ENDPOINTS.top_level_sections.delete_section(id)
    );
  },
};

export const usePostDuplicateCategory = {
  usePostDuplicateCategory: () => {
    return useMutation(payload =>
      API_ENDPOINTS.duplicate_category.duplicate_category(payload)
    );
  },
};

export const usePostDuplicateCategoryLineItem = {
  usePostDuplicateCategoryLineItem: () => {
    return useMutation(payload =>
      API_ENDPOINTS.duplicate_category_line_item.duplicate_category_line_item(
        payload
      )
    );
  },
};

export const useLinkedFringesMutation = {
  useLinkFringesMutation: () => {
    return useMutation(payload =>
      API_ENDPOINTS.linked_fringes.link_fringes(payload)
    );
  },
  useUnlinkFringesMutation: () => {
    return useMutation(payload => API_ENDPOINTS.linked_fringes.unlink(payload));
  },
};
